import React, {useEffect, useState} from 'react';
import GeneralActions from "../store/general/actions";
import {connect, useDispatch} from "react-redux";
import ProviderAPI from "../services/Provider";
import {Redirect, Route, Switch, withRouter} from 'react-router-dom';
import ConfirmModal from "../components/ConfirmModal";
import {localize} from "../services/translation/localization";
import InfoModal from "../components/InfoModal";
import Enums from "../services/Enums";
import {locate} from "../services/locations";

const customStyle = () => ({
    container: {width: 'fit-content'},
    btn_container: {margin: '2em'},
    btn_deny: {marginRight: '0.5em'},
    btn_confirm: {marginLeft: '0.5em'},

})

const AppContainer = ({children, location, history, match, countryDetails, ...props}) => {
    const [loading, setLoading] = useState(true)
    const [resendTokenModal, setResendTokenModal] = useState(false)
    const [resendMethodModal, setResendMethodModal] = useState(false)
    const [newLinkSent, setNewLinkSent] = useState(null)
    const dispatch = useDispatch();

    const getCountries = async () => {
        const countries = ProviderAPI.countries;

        const countriesByAlpha2 = countries.reduce((acc, curr) => {
            acc[curr.Alpha2] = curr;
            return acc;
        }, {})

        dispatch(GeneralActions.setCountries(countriesByAlpha2));

        return countriesByAlpha2;
    }

    const getCountryFromLocation = async (countries, location) => {
        let countryAlpha2FromLocation = location?.address?.countryCode;

        if (!countryAlpha2FromLocation) {
            const _location = await locate();
            countryAlpha2FromLocation = _location?.address?.countryCode
        }

        let selectedCountry = countries[countryAlpha2FromLocation];

        if (!selectedCountry) {
            // fallback to GB if the country is not supported
            selectedCountry = countries['GB'];
        }

        return {...selectedCountry, urlCountryManual: true}
    }

    const countrySetup = async (countries, location) => {
        let countryAlpha2FromUrl = match.params.country;
        let country;
        if (!countryAlpha2FromUrl) {
            country = await getCountryFromLocation(countries, location)
        } else {
            countryAlpha2FromUrl = countryAlpha2FromUrl.toUpperCase();
            const selectedCountry = countryAlpha2FromUrl === 'UK' ?
                countries['GB'] : countries[countryAlpha2FromUrl];
            // set selected country OR get it from location if the one in the url is invalid/unsupported
            country = selectedCountry || await getCountryFromLocation(countries, location);
        }

        dispatch(GeneralActions.setCountryDetails(country));
        return country;
    }

    const auth = async (country, countries) => {
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');
        if (token) {
            const res = await ProviderAPI.exchangeRequestToken(token);

            if (res.message === `Expired Token`) {
                setResendTokenModal(localize(null, 'expiredToken'));
            } else if (!res.token) {
                setResendTokenModal(localize(null, 'invalidToken'));
            }
        } else {
            const providerRes = await ProviderAPI.get('ServiceProviders');

            const isLoginPath = !!location.pathname.match(/^\/[a-zA-Z]{2}\/login(\/)?$/)
            if (providerRes && providerRes.value && providerRes.value[0] && location.pathname.length > 4 && !isLoginPath) {
                const provider = providerRes.value[0];
                const providerCountry = Object.values(countries).find(c => c.Id === provider.CountryId)
                dispatch(GeneralActions.setCountryDetails(providerCountry));
                const url = `/${providerCountry.Alpha2}${location.pathname.slice(3)}${location.search}`
                history.replace(url);
            }
            if (!providerRes || !providerRes.value || !providerRes.value[0] || location.pathname === '' || location.pathname === '/') {
                history.replace(`/${country}${location.search}`);
            }
        }
    }

    const init = async () => {
        Enums.init().then();

        locate().then((location) => {
            dispatch(GeneralActions.setGeoLocationData(location));
        });

        const countries = await getCountries();
        const {Alpha2} = await countrySetup(countries, location);
        await auth(Alpha2, countries);
        await setAffiliate();
        setLoading(false);
    }

    useEffect(() => {
        init().then();
    }, [])

    const setAffiliate = async () => {
        const urlParams = new URLSearchParams(location.search);
        const affiliateHash = urlParams.get('affiliate');
        if (!affiliateHash) {
            return;
        }

        const affiliate = await ProviderAPI.get(`api/provider/affiliate/${affiliateHash}`);
        if (!affiliate || !affiliate.Id) {
            return;
        }

        localStorage.setItem('affiliate', JSON.stringify({conversionTTL: affiliate.ConversionTTL, affiliateHash, openingTime: new Date().getTime()}));
    }

    const resendToken = async (messageType) => {
        setLoading(true);
        const providerId = location.pathname.split('/').pop();

        const messageTypes = {
            email: 1,
            sms: 2,
        };

        if (providerId) {
            await ProviderAPI.resendToken(providerId, location.pathname.slice(1), messageTypes[messageType]);
        }

        setNewLinkSent(`Link sent by ${messageType}.`);
        setLoading(false);
        setResendTokenModal(false);
        setResendMethodModal(false);
    }

    if (loading || !countryDetails) return null;

    let redirectPath = '/GB';
    if (location.pathname.toLowerCase().startsWith('/uk')) {
        redirectPath = location.pathname.replace(/^\/uk/i, '/GB');
    }

    return (
        <>
            <Switch>
                <Route path="/uk">
                    <Redirect to={redirectPath}/>
                </Route>
            </Switch>
            {!resendTokenModal && !resendMethodModal && !newLinkSent && children}

            <ConfirmModal onConfirm={() => resendToken('email')}
                          customStyle={customStyle()}
                          onDeny={() => resendToken('sms')}
                          isOpen={!!resendTokenModal}
                          confirmLabel={localize(null, 'Email')}
                          denyLabel={localize(null, 'SMS')}
                          sameButtons={true}
                          message={resendTokenModal}/>

            <InfoModal message={newLinkSent}/>
        </>
    )

}

const mapStateToProps = state => ({
    countryDetails: state.general.countryDetails,
    countries: state.general.countries,
});

const mapDispatchToProps = {
    setPackages: GeneralActions.setPackages,
    setCountryDetails: GeneralActions.setCountryDetails,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AppContainer));
